import React, { useCallback, useMemo } from 'react';
import loadable from '@loadable/component';
import { useDispatch, useSelector } from 'react-redux';
import LazyLoader from 'react-lazyload';
import { useTranslationContext } from '@pegasus/translation';
import { useConfig } from '@browsing/config';
import { getQueryParam } from '@browsing/utils';

import { RootState } from '@client/redux/types';
import { getClientDeviceHeight } from '@client/redux/modules/clientDevice';
import { makeGetUpcomingCampaigns } from '@client/redux/modules/mylounge/selectors';
import { track } from '@client/helpers/tracking';
import {
    getFirstName,
    getIsSubscribedToNewsletter,
} from '@client/redux/modules/customer/selectors';
import { FLASH_MESSAGE_TYPE_SUCCESS } from '@client/config/constants';
import {
    showHeaderErrorFlashMessage,
    showHeaderFlashMessage,
    showHeaderSuccessFlashMessage,
} from '@client/redux/modules/flashMessage';
import { getCookie } from '@client/helpers/cookie';
import { useZalandoPlus } from '@client/components/mylounge/plus';

const UpcomingSection = loadable(
    () => import(/* webpackChunkName: "UpcomingSection" */ '@pegasus/upcoming-section'),
);

const UpcomingSectionWrapper: React.FC<{
    hasTitle?: boolean;
    isHorizontal?: boolean;
    customHighlightSection?: React.ReactNode;
}> = ({ hasTitle, isHorizontal = false, customHighlightSection }) => {
    const { translations } = useTranslationContext();
    const deviceHeight = useSelector(getClientDeviceHeight);
    const firstName = useSelector(getFirstName);
    const config = useConfig();
    const dispatch = useDispatch();
    const notificationEnabled = useMemo(
        () => Boolean(config?.isEnabled('pushNotification') || getCookie('zl_web_push')),
        [config],
    );

    const { isEnabled: isPlusEnabled, isSignupEnabled } = useZalandoPlus();

    const getUpcomingCampaigns = useMemo(makeGetUpcomingCampaigns, []);
    const upcomingCampaigns = useSelector((state: RootState) =>
        getUpcomingCampaigns(state, isSignupEnabled),
    );

    const isSubscribedToNewsletter = useSelector(getIsSubscribedToNewsletter);

    const reminderPopupValue = useMemo(() => {
        const eventValue = getQueryParam('event');
        if (typeof eventValue !== 'string') {
            return null;
        }

        return eventValue;
    }, []);

    const handlePushAPIActivationFinished = useCallback(
        (isActivated: boolean, message: string) => {
            const showFlashMessageFn = isActivated
                ? showHeaderSuccessFlashMessage
                : showHeaderErrorFlashMessage;
            dispatch(showFlashMessageFn(message));
        },
        [dispatch],
    );

    const handleCountdown = useCallback(
        (message: string, callbackText: string) => {
            dispatch(
                showHeaderFlashMessage({
                    type: FLASH_MESSAGE_TYPE_SUCCESS,
                    message,
                    callbackText,
                    clickCallback: () => window.location.reload(),
                }),
            );
        },
        [dispatch],
    );

    const upcomingSectionProps = {
        hasTitle,
        firstName,
        isNotificationEnabled: notificationEnabled,
        isPlusEnabled,
        isPlusSignupEnabled: isSignupEnabled,
        upcomingCampaigns,
        showNewsletterImage: !customHighlightSection && !isSubscribedToNewsletter,
        notificationTagSuffix: config.notificationTagSuffix,
        appDomain: config.appdomainid,
        campaignIdToHighlight: reminderPopupValue,
        translations,
        track,
        onCountdownFinish: handleCountdown,
        onPushAPIActivationFinished: handlePushAPIActivationFinished,
        horizontal: isHorizontal,
        customHighlightSection,
    };

    if (getQueryParam('status') === 'upcoming') {
        return (
            <div id="lazyload-campaigns-upcoming">
                <UpcomingSection {...upcomingSectionProps} />
            </div>
        );
    }

    return (
        <div id="lazyload-campaigns-upcoming">
            <LazyLoader offset={deviceHeight * 2} once>
                <UpcomingSection {...upcomingSectionProps} />
            </LazyLoader>
        </div>
    );
};

export default UpcomingSectionWrapper;
